/**
 * M9 — clear the air. An offer to open a room with a named person, once;
 * or a paused room with them waiting to be resumed. Declining ("not that
 * kind of thing") is the 30-day dismiss for that person — the two-turn
 * handshake rule (PPL-388) applied to home.
 *
 * The primary tap is `open`; the host's route map opens the rooms composer
 * with the person preselected, or resumes the paused room.
 */

import { HomeAction, HomeActions, HomeCard } from '../HomeKit';
import type { ModuleProps } from '../moduleTypes';

export function ClearAirModule({ module, onAction }: ModuleProps) {
  if (module.type !== 'clear_air') return null;
  const { payload } = module;
  const act = (a: Parameters<NonNullable<typeof onAction>>[0]) => onAction?.(a, module);
  const paused = payload.variant === 'paused' && payload.mediationId;
  const name = payload.personName;

  const open = () => act({ kind: 'open' });
  const dismiss = module.dismissable ? () => act({ kind: 'dismiss', dismissKey: module.dismissKey }) : undefined;

  return (
    <HomeCard
      reason={module.reason}
      body={paused ? `Your room with ${name} is waiting` : `Want to clear the air with ${name}?`}
      sub={paused ? 'resume when you’re ready' : 'a private room, one step at a time'}
      dismissable={module.dismissable}
      onDismiss={dismiss}
      onOpen={open}
      secondary={
        <HomeActions>
          <HomeAction primary onClick={open}>{paused ? 'resume the room' : 'open a room'}</HomeAction>
          {dismiss ? <HomeAction onClick={dismiss}>{paused ? 'not now' : 'not that kind of thing'}</HomeAction> : null}
        </HomeActions>
      }
    />
  );
}
